import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { Patient, Encounter, Prescription } from '../types';
import { PillIcon, CheckCircleIcon, XCircleIcon, PrinterIcon, ArrowLeftIcon, Undo2Icon, CheckIcon } from 'lucide-react';
export const PharmacyDashboard: React.FC = () => {
  const {
    patients,
    updateEncounter
  } = useData();
  const {
    user
  } = useAuth();
  const [selectedEncounter, setSelectedEncounter] = useState<{
    patient: Patient;
    encounter: Encounter;
  } | null>(null);
  const liveEncounter = selectedEncounter ? (() => {
    const p = patients.find(pt => pt.id === selectedEncounter.patient.id);
    if (!p) return selectedEncounter;
    const enc = p.encounters.find(e => e.id === selectedEncounter.encounter.id);
    if (!enc) return selectedEncounter;
    return {
      patient: p,
      encounter: enc
    };
  })() : null;
  const pharmacyQueue = patients.flatMap(p => p.encounters.filter(e => e.status === 'Pharmacy').map(e => ({
    patient: p,
    encounter: e
  })));
  const handleUpdatePrescription = (prescriptionId: string, updates: Partial<Prescription>) => {
    if (!liveEncounter) return;
    const {
      patient,
      encounter
    } = liveEncounter;
    const updatedPrescriptions = encounter.prescriptions.map(p => p.id === prescriptionId ? {
      ...p,
      ...updates
    } : p);
    const updatedEncounter: Encounter = {
      ...encounter,
      prescriptions: updatedPrescriptions
    };
    updateEncounter(patient.id, updatedEncounter);
    setSelectedEncounter({
      patient,
      encounter: updatedEncounter
    });
  };
  const handleComplete = () => {
    if (!liveEncounter) return;
    const {
      patient,
      encounter
    } = liveEncounter;
    const updatedEncounter: Encounter = {
      ...encounter,
      status: 'Completed'
    };
    updateEncounter(patient.id, updatedEncounter);
    setSelectedEncounter(null);
  };
  const buildPrintHtml = (patientName: string, fileNo: string, age: string, sex: string, doctorName: string, pharmacistName: string, drugs: Prescription[]): string => {
    const drugRows = drugs.map((drug, i) => '<tr>' + '<td>' + (i + 1) + '</td>' + '<td><strong>' + drug.drugName + '</strong></td>' + '<td>' + drug.dose + '</td>' + '<td>' + drug.route + '</td>' + '<td>' + drug.frequency + '</td>' + '<td>' + drug.days + '</td>' + '</tr>').join('');
    return '<!DOCTYPE html>' + '<html><head>' + '<title>Prescription - ' + patientName + '</title>' + '<style>{`' + 'body{font-family:Helvetica Neue,Helvetica,Arial,sans-serif;padding:40px;color:#1f2937;line-height:1.5}' + '.hdr{display:flex;align-items:center;border-bottom:2px solid #0f766e;padding-bottom:20px;margin-bottom:30px}' + '.hdr img{width:100px;height:auto;margin-right:20px}' + '.hdr-txt{flex-grow:1;text-align:center}' + '.hdr-txt h1{margin:0;color:#0f766e;font-size:24px;letter-spacing:1px}' + '.hdr-txt p{margin:5px 0 0;font-size:14px;color:#4b5563}' + '.pinfo{display:grid;grid-template-columns:1fr 1fr;gap:15px;margin-bottom:30px;background:#f9fafb;padding:20px;border-radius:8px;border:1px solid #e5e7eb}' + '.pinfo div{font-size:14px}' + '.pinfo strong{color:#0f766e;display:inline-block;width:100px}' + 'table{width:100%;border-collapse:collapse;margin-bottom:40px}' + 'th,td{border:1px solid #d1d5db;padding:12px;text-align:left;font-size:14px}' + 'th{background:#f0fdfa;color:#0f766e;font-weight:bold}' + '.ftr{margin-top:60px;display:flex;justify-content:space-between}' + '.sig{text-align:center;width:250px}' + '.sig-line{border-top:1px solid #1f2937;margin-top:50px;padding-top:8px;font-size:14px;font-weight:bold}' + '.sig-sub{font-size:12px;color:#6b7280;font-weight:normal}' + '.wm{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-45deg);font-size:80px;color:rgba(15,118,110,0.05);z-index:-1;white-space:nowrap;pointer-events:none}' + '`}</style>' + '</head><body>' + '<div class="wm">EXTERNAL PRESCRIPTION</div>' + '<div class="hdr">' + '<img src="https://cdn.magicpatterns.com/uploads/d6s42WEUAvLVPoFhjsD/Copy_of_Clean_ElegantTransparent_Logo_(3).png" alt="BMC Logo"/>' + '<div class="hdr-txt"><h1>EXTERNAL PRESCRIPTION FORM</h1><p>BmedCare Medical Centre &bull; Excellence in Healthcare</p></div>' + '</div>' + '<div class="pinfo">' + '<div><strong>Patient:</strong> ' + patientName + '</div>' + '<div><strong>Date:</strong> ' + new Date().toLocaleDateString() + '</div>' + '<div><strong>File No:</strong> ' + fileNo + '</div>' + '<div><strong>Age/Sex:</strong> ' + age + ' / ' + sex + '</div>' + '</div>' + '<table><thead><tr>' + '<th style="width:5%">No.</th><th style="width:35%">Drug Name</th><th style="width:15%">Dose</th><th style="width:15%">Route</th><th style="width:15%">Frequency</th><th style="width:15%">Days</th>' + '</tr></thead><tbody>' + drugRows + '</tbody></table>' + '<div class="ftr">' + '<div class="sig"><div class="sig-line">' + doctorName + '<div class="sig-sub">Clinician Signature</div></div></div>' + '<div class="sig"><div class="sig-line">' + pharmacistName + '<div class="sig-sub">Pharmacist Signature</div></div></div>' + '</div>' + '</body></html>';
  };
  const handlePrintUnavailable = () => {
    if (!liveEncounter) return;
    const {
      patient,
      encounter
    } = liveEncounter;
    const unavailableDrugs = encounter.prescriptions.filter(p => p.notAvailable);
    if (unavailableDrugs.length === 0) {
      alert('No unavailable drugs to print.');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the prescription.');
      return;
    }
    const html = buildPrintHtml(patient.personalDetails.fullNames, patient.personalDetails.fileNo, patient.personalDetails.age, patient.personalDetails.sex, encounter.doctorName || 'Prescribing Doctor', user?.name || 'Pharmacist', unavailableDrugs);
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 600);
  };
  // ─── DETAIL VIEW ───
  if (liveEncounter) {
    const {
      patient,
      encounter
    } = liveEncounter;
    const allMarked = encounter.prescriptions.length > 0 && encounter.prescriptions.every(p => p.dispensed || p.notAvailable);
    const hasUnavailable = encounter.prescriptions.some(p => p.notAvailable);
    const dispensedCount = encounter.prescriptions.filter(p => p.dispensed).length;
    const unavailableCount = encounter.prescriptions.filter(p => p.notAvailable).length;
    const pendingCount = encounter.prescriptions.length - dispensedCount - unavailableCount;
    return <div className="bg-gray-100 min-h-screen pb-12" data-id="element-832">
        {/* Sticky Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 p-4 sticky top-0 z-20 flex justify-between items-center" data-id="element-833">
          <div className="flex items-center" data-id="element-834">
            <button onClick={() => setSelectedEncounter(null)} className="mr-4 p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-full transition-colors" data-id="element-835">
              <ArrowLeftIcon className="h-5 w-5" data-id="element-836" />
            </button>
            <div data-id="element-837">
              <h2 className="text-lg font-bold text-gray-800" data-id="element-838">
                Prescription Dispensing
              </h2>
              <p className="text-sm text-gray-500" data-id="element-839">
                {patient.personalDetails.fullNames} &bull; File:{' '}
                {patient.personalDetails.fileNo}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3" data-id="element-840">
            {hasUnavailable && <button onClick={handlePrintUnavailable} className="inline-flex items-center px-4 py-2 bg-white border-2 border-orange-300 text-orange-700 text-sm font-bold rounded-lg hover:bg-orange-50 transition-colors" data-id="element-841">
                <PrinterIcon className="mr-2 h-4 w-4" data-id="element-842" /> Print Unavailable
              </button>}
            <button onClick={handleComplete} disabled={!allMarked} className={`inline-flex items-center px-5 py-2 text-sm font-bold rounded-lg transition-colors ${allMarked ? 'bg-teal-600 text-white hover:bg-teal-700 shadow-md' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-843">
              <CheckCircleIcon className="mr-2 h-5 w-5" data-id="element-844" /> Complete Dispensing
            </button>
          </div>
        </div>

        <div className="max-w-6xl mx-auto mt-6 px-4" data-id="element-845">
          {/* Summary Badges */}
          <div className="flex gap-3 mb-6" data-id="element-846">
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 flex-1 text-center shadow-sm" data-id="element-847">
              <p className="text-2xl font-bold text-gray-800" data-id="element-848">
                {encounter.prescriptions.length}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-849">Total Drugs</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex-1 text-center shadow-sm" data-id="element-850">
              <p className="text-2xl font-bold text-green-700" data-id="element-851">
                {dispensedCount}
              </p>
              <p className="text-xs text-green-600 mt-1" data-id="element-852">Dispensed</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex-1 text-center shadow-sm" data-id="element-853">
              <p className="text-2xl font-bold text-red-700" data-id="element-854">
                {unavailableCount}
              </p>
              <p className="text-xs text-red-600 mt-1" data-id="element-855">Not Available</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex-1 text-center shadow-sm" data-id="element-856">
              <p className="text-2xl font-bold text-yellow-700" data-id="element-857">
                {pendingCount}
              </p>
              <p className="text-xs text-yellow-600 mt-1" data-id="element-858">Pending</p>
            </div>
          </div>

          {/* Prescription Card */}
          <div className="bg-white shadow-lg border border-gray-200 rounded-lg overflow-hidden" data-id="element-859">
            {/* Card Header */}
            <div className="bg-teal-50 border-b border-teal-100 p-6 flex justify-between items-start" data-id="element-860">
              <div data-id="element-861">
                <h3 className="text-lg font-bold text-teal-900 uppercase tracking-wider mb-1" data-id="element-862">
                  Prescription Order
                </h3>
                <p className="text-sm text-teal-700" data-id="element-863">
                  Prescribed by:{' '}
                  <span className="font-semibold" data-id="element-864">{encounter.doctorName}</span>{' '}
                  on {new Date(encounter.date).toLocaleDateString()}
                </p>
              </div>
              <PillIcon className="h-10 w-10 text-teal-200" data-id="element-865" />
            </div>

            {/* Drugs Table */}
            <div className="p-6" data-id="element-866">
              <div className="overflow-x-auto border border-gray-200 rounded-md" data-id="element-867">
                <table className="w-full text-sm text-left" data-id="element-868">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-869">
                    <tr data-id="element-870">
                      <th className="p-3 font-semibold text-gray-600 w-12" data-id="element-871">
                        No.
                      </th>
                      <th className="p-3 font-semibold text-gray-600" data-id="element-872">
                        Drug Name
                      </th>
                      <th className="p-3 font-semibold text-gray-600 w-24" data-id="element-873">
                        Dose
                      </th>
                      <th className="p-3 font-semibold text-gray-600 w-24" data-id="element-874">
                        Route
                      </th>
                      <th className="p-3 font-semibold text-gray-600 w-28" data-id="element-875">
                        Frequency
                      </th>
                      <th className="p-3 font-semibold text-gray-600 w-20" data-id="element-876">
                        Days
                      </th>
                      <th className="p-3 font-semibold text-gray-600 w-28" data-id="element-877">
                        Prescriber
                      </th>
                      <th className="p-3 font-semibold text-gray-600 w-32" data-id="element-878">
                        Status
                      </th>
                      <th className="p-3 font-semibold text-gray-600 w-44 text-center" data-id="element-879">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-880">
                    {encounter.prescriptions.map((drug, index) => {
                    const isMarked = drug.dispensed || drug.notAvailable;
                    return <tr key={drug.id} className={`transition-colors ${drug.dispensed ? 'bg-green-50/40' : drug.notAvailable ? 'bg-red-50/40' : 'hover:bg-gray-50'}`} data-id="element-881">
                          <td className="p-3 text-gray-400 font-mono" data-id="element-882">
                            {index + 1}
                          </td>
                          <td className="p-3 font-bold text-gray-900" data-id="element-883">
                            {drug.drugName}
                          </td>
                          <td className="p-3 text-gray-700" data-id="element-884">{drug.dose}</td>
                          <td className="p-3 text-gray-700" data-id="element-885">{drug.route}</td>
                          <td className="p-3 text-gray-700" data-id="element-886">
                            {drug.frequency}
                          </td>
                          <td className="p-3 text-gray-700" data-id="element-887">{drug.days}</td>
                          <td className="p-3 text-gray-500 italic text-xs" data-id="element-888">
                            {drug.signature}
                          </td>
                          <td className="p-3" data-id="element-889">
                            {drug.dispensed && <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800" data-id="element-890">
                                <CheckIcon className="mr-1 h-3 w-3" data-id="element-891" /> Dispensed
                              </span>}
                            {drug.notAvailable && <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800" data-id="element-892">
                                <XCircleIcon className="mr-1 h-3 w-3" data-id="element-893" />{' '}
                                Unavailable
                              </span>}
                            {!isMarked && <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-894">
                                Pending
                              </span>}
                          </td>
                          <td className="p-3" data-id="element-895">
                            {!isMarked ? <div className="flex space-x-2 justify-center" data-id="element-896">
                                <button onClick={() => handleUpdatePrescription(drug.id, {
                            dispensed: true,
                            notAvailable: false,
                            dispensedBy: user?.name
                          })} className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs font-semibold transition-colors shadow-sm" data-id="element-897">
                                  <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-898" />{' '}
                                  Dispense
                                </button>
                                <button onClick={() => handleUpdatePrescription(drug.id, {
                            dispensed: false,
                            notAvailable: true,
                            dispensedBy: user?.name
                          })} className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-xs font-semibold transition-colors shadow-sm" data-id="element-899">
                                  <XCircleIcon className="mr-1 h-3 w-3" data-id="element-900" /> Not
                                  Avail
                                </button>
                              </div> : <div className="flex justify-center" data-id="element-901">
                                <button onClick={() => handleUpdatePrescription(drug.id, {
                            dispensed: false,
                            notAvailable: false,
                            dispensedBy: undefined
                          })} className="inline-flex items-center px-3 py-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-md text-xs font-medium transition-colors" data-id="element-902">
                                  <Undo2Icon className="mr-1 h-3 w-3" data-id="element-903" /> Undo
                                </button>
                              </div>}
                          </td>
                        </tr>;
                  })}
                    {encounter.prescriptions.length === 0 && <tr data-id="element-904">
                        <td colSpan={9} className="p-8 text-center text-gray-400" data-id="element-905">
                          No prescriptions found for this encounter.
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer with pharmacist name */}
            <div className="bg-gray-50 p-6 border-t border-gray-200 flex justify-between items-end" data-id="element-906">
              <div data-id="element-907">
                {hasUnavailable && <p className="text-sm text-orange-600 font-medium" data-id="element-908">
                    {unavailableCount} drug(s) not available — use "Print
                    Unavailable" to generate an external prescription.
                  </p>}
              </div>
              <div className="text-right" data-id="element-909">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-2" data-id="element-910">
                  Dispensed by
                </p>
                <p className="text-base font-bold text-gray-900 border-b-2 border-teal-600 pb-1 px-6 inline-block" data-id="element-911">
                  {user?.name || 'Pharmacist'}
                </p>
                <p className="text-xs text-gray-400 mt-1" data-id="element-912">Pharmacist</p>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // ─── QUEUE VIEW ───
  return <div className="space-y-6" data-id="element-913">
      <div className="flex items-center justify-between" data-id="element-914">
        <h2 className="text-xl font-bold text-gray-800" data-id="element-915">Pharmacy Queue</h2>
        <span className="bg-teal-100 text-teal-800 text-xs font-bold px-3 py-1.5 rounded-full" data-id="element-916">
          {pharmacyQueue.length} Pending
        </span>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-100" data-id="element-917">
        <ul className="divide-y divide-gray-200" data-id="element-918">
          {pharmacyQueue.map(item => <li key={item.encounter.id} data-id="element-919">
              <div className="px-5 py-4 hover:bg-gray-50 flex items-center justify-between transition-colors" data-id="element-920">
                <div className="flex items-center" data-id="element-921">
                  <div className="flex-shrink-0 bg-teal-50 p-2.5 rounded-full" data-id="element-922">
                    <PillIcon className="h-6 w-6 text-teal-600" data-id="element-923" />
                  </div>
                  <div className="ml-4" data-id="element-924">
                    <p className="text-sm font-bold text-teal-700" data-id="element-925">
                      {item.patient.personalDetails.fullNames}
                    </p>
                    <div className="flex items-center mt-1 text-xs text-gray-500 space-x-2" data-id="element-926">
                      <span data-id="element-927">File: {item.patient.personalDetails.fileNo}</span>
                      <span className="text-gray-300" data-id="element-928">|</span>
                      <span data-id="element-929">Dr. {item.encounter.doctorName}</span>
                      <span className="text-gray-300" data-id="element-930">|</span>
                      <span data-id="element-931">
                        {new Date(item.encounter.date).toLocaleDateString()}
                      </span>
                      <span className="text-gray-300" data-id="element-932">|</span>
                      <span className="font-medium text-teal-600" data-id="element-933">
                        {item.encounter.prescriptions.length} drug(s)
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelectedEncounter(item)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors" data-id="element-934">
                  Open Prescription
                </button>
              </div>
            </li>)}
          {pharmacyQueue.length === 0 && <li className="px-4 py-14 text-center" data-id="element-935">
              <PillIcon className="mx-auto h-12 w-12 text-gray-200 mb-3" data-id="element-936" />
              <p className="text-gray-400 text-base font-medium" data-id="element-937">
                No pending prescriptions
              </p>
              <p className="text-gray-300 text-sm mt-1" data-id="element-938">
                Prescriptions sent by clinicians will appear here.
              </p>
            </li>}
        </ul>
      </div>
    </div>;
};